import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as ResolveIcon,
  Send as SendIcon
} from '@mui/icons-material';
import useProcessFlowStore from '../../stores/processFlowStore';
import { useSnackbar } from 'notistack';

function CommentPanel({ flowId, selectedNodeId, onClose }) {
  const { currentFlow, addComment, resolveComment } = useProcessFlowStore();
  const { enqueueSnackbar } = useSnackbar();
  const [newComment, setNewComment] = useState('');

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await addComment(flowId, {
        text: newComment,
        nodeId: selectedNodeId
      });
      setNewComment('');
      enqueueSnackbar('Comment added', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to add comment', { variant: 'error' });
    }
  };

  const handleResolveComment = async (commentId) => {
    try {
      await resolveComment(flowId, commentId);
      enqueueSnackbar('Comment resolved', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to resolve comment', { variant: 'error' });
    }
  };

  const comments = currentFlow?.comments || [];
  const filteredComments = selectedNodeId
    ? comments.filter(c => c.nodeId === selectedNodeId)
    : comments;

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={onClose}
      PaperProps={{ sx: { width: 360 } }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Comments {selectedNodeId && <Chip label="Node" size="small" />}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <List sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
          {filteredComments.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center">
              No comments yet
            </Typography>
          ) : (
            filteredComments.map((comment) => (
              <ListItem
                key={comment._id}
                alignItems="flex-start"
                secondaryAction={
                  !comment.resolved && (
                    <IconButton
                      edge="end"
                      aria-label="resolve"
                      onClick={() => handleResolveComment(comment._id)}
                      size="small"
                    >
                      <ResolveIcon />
                    </IconButton>
                  )
                }
              >
                <ListItemAvatar>
                  <Avatar sx={{ width: 32, height: 32 }}>
                    {comment.userId?.firstName?.[0]}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">
                        {comment.userId?.firstName} {comment.userId?.lastName}
                      </Typography>
                      {comment.resolved && (
                        <Chip label="Resolved" size="small" color="success" />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {comment.text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(comment.createdAt).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))
          )}
        </List>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddComment();
              }
            }}
            multiline
            maxRows={4}
          />
          <IconButton
            color="primary"
            onClick={handleAddComment}
            disabled={!newComment.trim()}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Drawer>
  );
}

export default CommentPanel;